/* =========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* =========================================================================== */

/*
 * โควตา AI รายยศ/รายคนต่อเดือน + การใช้เดือนนี้รายคน/รายยศ (ตรวจการใช้งาน 2.2 · 1 พ.ย. 2569 · migration 0046)
 *   ใช้ในหน้า AI ค่าใช้จ่ายและโควตา เฉพาะผู้มีสิทธิ์ ai_setting · แถวรายคนชนะรายยศ · ไม่ตั้ง = ไม่จำกัด (เหลือโควตาทั้งร้าน)
 *   OPTIONS /ai_report/quota · quota_users {search} · quota_save {scope, ref_id, monthly_credits ('' = เอาออก)}
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const num = (v) => Func.numberDisplay(Number(v || 0), 0);

export default function AiQuotaPanel({ token, onNotify }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ scope: 'ROLE', role: '', user: null, credits: '' });
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [ask, setAsk] = useState(null); // ยืนยันก่อน ตั้ง 0 / เอาโควตาออก
  const alive = useRef(true);
  const userTimer = useRef(null);

  const load = () =>
    FetchData('OPTIONS', '/ai_report/quota', token, { _t: Date.now() })
      .then((r) => {
        if (!alive.current) return;
        if (r && r.status === 'ok') {
          setData(r);
          setLoadFailed(false);
        } else setLoadFailed(true);
      })
      .catch(() => {
        if (alive.current) setLoadFailed(true);
      });

  useEffect(() => {
    alive.current = true;
    load();
    return () => {
      alive.current = false;
      clearTimeout(userTimer.current);
    };
  }, []);

  const searchUsers = (q) => {
    clearTimeout(userTimer.current);
    if (!q || q.length < 2) return;
    userTimer.current = setTimeout(() => {
      FetchData('OPTIONS', '/ai_report/quota_users', token, { search: q, _t: Date.now() })
        .then((r) => {
          if (alive.current && r && r.status === 'ok') setUsers(r.users || []);
        })
        .catch(() => {});
    }, 350);
  };

  const save = (scope, refId, credits) => {
    setBusy(true);
    FetchData('OPTIONS', '/ai_report/quota_save', token, {
      scope,
      ref_id: refId,
      monthly_credits: credits,
      _t: Date.now(),
    })
      .then((r) => {
        setBusy(false);
        if (r && r.status === 'ok') {
          onNotify(credits === '' ? 'เอาโควตาออกแล้ว' : 'บันทึกโควตาแล้ว', 'success');
          setForm({ scope, role: '', user: null, credits: '' });
          load();
        } else onNotify('บันทึกไม่สำเร็จ', 'error');
      })
      .catch(() => {
        setBusy(false);
        onNotify('บันทึกไม่สำเร็จ', 'error');
      });
  };

  if (!data && loadFailed)
    return (
      <Card sx={{ p: 2 }}>
        <MDTypography variant='h6'>โควตารายยศ / รายคน</MDTypography>
        <MDTypography variant='caption' color='error' display='block' mb={1}>
          โหลดโควตาไม่สำเร็จ
        </MDTypography>
        <MDBox>
          <MDButton size='small' variant='outlined' color='info' onClick={load}>
            ลองใหม่
          </MDButton>
        </MDBox>
      </Card>
    );
  if (!data) return null;
  if (data.ready !== 'Y')
    return (
      <Card sx={{ p: 2 }}>
        <MDTypography variant='h6'>โควตารายยศ / รายคน</MDTypography>
        <MDTypography variant='caption' color='warning'>
          ยังไม่ได้รันไฟล์ปรับฐาน 0046 — แจ้งไอที
        </MDTypography>
      </Card>
    );

  /* โควตายศบังคับต่อคน (AiImage::userQuota) — เทียบกับคนที่ใช้สูงสุดในยศ ไม่ใช่ยอดรวมทั้งยศ */
  const usedBy = {};
  (data.by_user || []).forEach((u) => {
    usedBy[`USER:${u.user_id}`] = Number(u.credits || 0);
    const key = `ROLE:${u.role_id}`;
    usedBy[key] = Math.max(usedBy[key] || 0, Number(u.credits || 0));
  });
  const limitOf = {};
  (data.limits || []).forEach((l) => {
    limitOf[`${l.scope}:${l.ref_id}`] = Number(l.monthly_credits);
  });
  const refId = form.scope === 'ROLE' ? form.role : form.user && form.user.id;
  const credits = String(form.credits).trim();
  const canSave = data.can_edit === 'Y' && refId && /^\d+$/.test(credits);
  const scopeText = form.scope === 'ROLE' ? 'แต่ละคนในยศนี้' : 'ผู้ใช้คนนี้';
  const trySave = () => {
    if (Number(credits) === 0)
      setAsk({
        title: 'ตั้งเป็น 0 เครดิต?',
        text: `${scopeText}จะใช้ AI ไม่ได้เลยในเดือนนี้`,
        okLabel: 'ตั้งเป็น 0',
        run: () => save(form.scope, refId, '0'),
      });
    else save(form.scope, refId, String(Number(credits)));
  };

  return (
    <Card sx={{ p: 2 }}>
      <MDTypography variant='h6'>โควตารายยศ / รายคน (เครดิตต่อเดือน)</MDTypography>
      <MDTypography variant='caption' color='text' display='block'>
        คนเดียวใช้โควตาทั้งร้านหมดไม่ได้ · ตั้งตามยศ = แต่ละคนในยศได้คนละเท่านี้ · ตั้งรายคนชนะรายยศ
        · ไม่ตั้ง = ไม่จำกัด · 0 = ใช้ AI ไม่ได้
      </MDTypography>
      <Divider />
      {data.can_edit === 'Y' ? (
        <Grid container spacing={2} alignItems='flex-end' mb={1}>
          <Grid item xs={12} sm={3}>
            <MDInput
              select
              fullWidth
              variant='standard'
              label='ตั้งให้'
              value={form.scope}
              onChange={(e) => setForm({ ...form, scope: e.target.value })}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value='ROLE'>ทุกคนในยศ (คนละเท่ากัน)</MenuItem>
              <MenuItem value='USER'>รายคน</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={4}>
            {form.scope === 'ROLE' ? (
              <MDInput
                select
                fullWidth
                variant='standard'
                label='ยศ'
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                InputLabelProps={{ shrink: true }}
              >
                {(data.roles || []).map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </MDInput>
            ) : (
              <Autocomplete
                options={users}
                value={form.user}
                getOptionLabel={(o) => `${o.name} (${o.user_name}) · ${o.role_name}`}
                isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                onInputChange={(e, v) => searchUsers(v)}
                onChange={(e, v) => setForm({ ...form, user: v })}
                noOptionsText='พิมพ์ชื่อหรือชื่อผู้ใช้อย่างน้อย 2 ตัว'
                renderInput={(params) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <TextField {...params} variant='standard' label='ผู้ใช้' />
                )}
              />
            )}
          </Grid>
          <Grid item xs={8} sm={3}>
            <MDInput
              fullWidth
              variant='standard'
              type='number'
              label={form.scope === 'ROLE' ? 'เครดิตต่อคนต่อเดือน' : 'เครดิตต่อเดือน'}
              value={form.credits}
              onChange={(e) => setForm({ ...form, credits: e.target.value })}
              inputProps={{ min: 0 }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={4} sm={2}>
            <MDButton color='info' fullWidth disabled={busy || !canSave} onClick={trySave}>
              บันทึก
            </MDButton>
          </Grid>
        </Grid>
      ) : null}

      <MDBox sx={{ overflowX: 'auto' }}>
        <Table size='small'>
          <TableHead sx={{ display: 'table-header-group' }}>
            <TableRow>
              <TableCell>โควตาที่ตั้งไว้</TableCell>
              <TableCell align='right'>เครดิต/เดือน</TableCell>
              <TableCell align='right'>ใช้เดือนนี้ (ยศ = คนที่ใช้มากสุด)</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.limits || []).map((l) => (
              <TableRow key={l.id}>
                <TableCell>
                  {l.scope === 'ROLE' ? 'ยศ ' : ''}
                  {l.name || `#${l.ref_id}`}
                </TableCell>
                <TableCell align='right'>{num(l.monthly_credits)}</TableCell>
                <TableCell align='right'>{num(usedBy[`${l.scope}:${l.ref_id}`])}</TableCell>
                <TableCell align='right'>
                  {data.can_edit === 'Y' ? (
                    <MDButton
                      size='small'
                      variant='text'
                      color='error'
                      disabled={busy}
                      onClick={() =>
                        setAsk({
                          title: 'เอาโควตานี้ออก?',
                          text: `${l.scope === 'ROLE' ? 'ยศ ' : ''}${
                            l.name || `#${l.ref_id}`
                          } จะไม่จำกัดเครดิตรายคน (ยังนับรวมโควตาทั้งร้าน)`,
                          okLabel: 'เอาออก',
                          run: () => save(l.scope, l.ref_id, ''),
                        })
                      }
                    >
                      เอาออก
                    </MDButton>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
            {!(data.limits || []).length ? (
              <TableRow>
                <TableCell colSpan={4}>ยังไม่ได้ตั้ง — ใช้โควตาทั้งร้านอย่างเดียว</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </MDBox>

      <MDTypography variant='button' fontWeight='medium' display='block' mt={2}>
        ใช้เดือนนี้รายคน
      </MDTypography>
      <MDBox sx={{ overflowX: 'auto' }}>
        <Table size='small'>
          <TableHead sx={{ display: 'table-header-group' }}>
            <TableRow>
              <TableCell>ผู้ใช้</TableCell>
              <TableCell>ยศ</TableCell>
              <TableCell align='right'>ภาพ</TableCell>
              <TableCell align='right'>เครดิต</TableCell>
              <TableCell align='right'>โควตา</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.by_user || []).map((u) => {
              const lim = limitOf[`USER:${u.user_id}`] ?? limitOf[`ROLE:${u.role_id}`];
              return (
                <TableRow key={u.user_id}>
                  <TableCell>{u.name || `#${u.user_id}`}</TableCell>
                  <TableCell>{u.role_name}</TableCell>
                  <TableCell align='right'>{num(u.images)}</TableCell>
                  <TableCell align='right'>{num(u.credits)}</TableCell>
                  <TableCell align='right'>{lim === undefined ? 'ไม่จำกัด' : num(lim)}</TableCell>
                </TableRow>
              );
            })}
            {!(data.by_user || []).length ? (
              <TableRow>
                <TableCell colSpan={5}>เดือนนี้ยังไม่มีใครใช้ AI</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </MDBox>
      <Dialog open={Boolean(ask)} onClose={() => setAsk(null)} maxWidth='xs' fullWidth>
        {ask ? (
          <>
            <DialogTitle>{ask.title}</DialogTitle>
            <DialogContent>
              <MDTypography variant='body2' color='text'>
                {ask.text}
              </MDTypography>
            </DialogContent>
            <DialogActions>
              <MDButton variant='text' color='dark' autoFocus onClick={() => setAsk(null)}>
                กลับ
              </MDButton>
              <MDButton
                color='error'
                onClick={() => {
                  ask.run();
                  setAsk(null);
                }}
              >
                {ask.okLabel}
              </MDButton>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </Card>
  );
}

AiQuotaPanel.propTypes = {
  token: PropTypes.string.isRequired,
  onNotify: PropTypes.func.isRequired,
};
